import { taskAdminApis } from '@gleamer/apis';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import YAML from 'yaml';
import {
  ADMIN_TASKS_KEY,
  useAdminTasks,
} from '../../../../../hooks/useAdminTasks';
import { useAuthorization } from '../../../../../hooks/useImports';
import { PageButton } from '../../../../atoms/PageButton';
import { PageTitle } from '../../../../atoms/PageTitle';
import {
  Card,
  Field,
  FilePicker,
  inputClassName,
} from '../../../../atoms/Surface';
import { reviewersOf } from '../tasks';
import { EmailsInput } from './EmailsInput';
import {
  draftFromDocument,
  draftFromTask,
  DraftErrors,
  emptyDraft,
  observationsOf,
  readAnnotation,
  TaskDraft,
  toYaml,
  validate,
  withObservations,
} from './taskDraft';
import { ObservationsEditor } from './ObservationsEditor';

const { createAdminTask, fetchAdminTaskDetails, TaskCreationError } =
  taskAdminApis;

const textareaClassName = classNames(inputClassName, 'h-auto py-2');

function FieldError({ text }: { text?: string }) {
  return text ? <span className="text-sm text-dl-danger">{text}</span> : null;
}

function TextField({
  label,
  value,
  onChange,
  error,
  hint,
  placeholder,
  mono,
  list,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  placeholder?: string;
  mono?: boolean;
  /** The id of a datalist offering values; anything else can still be typed. */
  list?: string;
}) {
  return (
    <Field label={label} hint={error ? undefined : hint}>
      <input
        className={classNames(inputClassName, {
          'font-mono text-sm': mono,
          'ring-dl-danger': !!error,
        })}
        value={value}
        placeholder={placeholder}
        list={list}
        onChange={e => onChange(e.target.value)}
      />
      <FieldError text={error} />
    </Field>
  );
}

/** Where a new task starts: blank, a copy of an existing task, or a YAML file from Notion. */
function StartFrom({
  onDraft,
  origin,
  problem: given,
}: {
  onDraft: (draft: TaskDraft, origin: string) => void;
  origin: string;
  /** Why the task in the URL could not be copied. */
  problem?: string;
}) {
  const auth = useAuthorization();
  const tasks = useAdminTasks();
  const [choice, setChoice] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [problem, setProblem] = useState('');
  const options = useMemo(
    () =>
      (tasks.data?.content ?? []).map(t => ({
        id: t.taskId,
        label: `${t.name} (#${t.taskId})`,
      })),
    [tasks.data]
  );

  const copy = async () => {
    const picked = options.find(
      o => o.label === choice || String(o.id) === choice.trim()
    );
    if (!picked) {
      setProblem('Pick a task from the list.');
      return;
    }
    setProblem('');
    try {
      onDraft(
        draftFromTask(await fetchAdminTaskDetails(auth, picked.id)),
        `a copy of ${picked.label}`
      );
    } catch (e) {
      setProblem(`Could not read that task: ${(e as Error).message}`);
    }
  };

  const load = async (picked: File | null) => {
    setFile(picked);
    if (!picked) return;
    try {
      const doc = YAML.parse(await picked.text());
      if (!doc || typeof doc !== 'object')
        throw new Error('not a task document');
      setProblem('');
      onDraft(draftFromDocument(doc), picked.name);
    } catch (e) {
      setProblem(`Could not read ${picked.name}: ${(e as Error).message}`);
    }
  };

  return (
    <Card
      title="Start from"
      description={
        origin
          ? `Started from ${origin}. Everything below can be changed.`
          : 'A blank task, a copy of an existing one, or a YAML file you already have.'
      }
    >
      <div className="flex flex-wrap items-end gap-4">
        <PageButton
          className="mx-0 h-10 px-5"
          onClick={() => onDraft(emptyDraft(), 'a blank task')}
        >
          Blank task
        </PageButton>
        <span className="pb-2 text-dl-faint">or</span>
        <div className="flex items-end gap-2">
          <Field label="Copy a task">
            <input
              className={classNames(inputClassName, 'w-80')}
              list="copy-task-options"
              value={choice}
              placeholder="Name or id"
              onChange={e => setChoice(e.target.value)}
            />
          </Field>
          <datalist id="copy-task-options">
            {options.map(o => (
              <option key={o.id} value={o.label} />
            ))}
          </datalist>
          <PageButton
            className="mx-0 h-10 px-5"
            onClick={copy}
            disabled={!choice}
          >
            Copy
          </PageButton>
        </div>
        <span className="pb-2 text-dl-faint">or</span>
        <Field label="Import a YAML file">
          <FilePicker
            aria-label="Import a YAML file"
            accept=".yaml,.yml,application/x-yaml,text/yaml"
            file={file}
            label="Choose…"
            onChange={e => load(e.target.files?.[0] ?? null)}
          />
        </Field>
      </div>
      <FieldError text={problem || given} />
    </Card>
  );
}

/**
 * The annotation setup: the observations as cards, or the whole YAML (observations, characterisations,
 * layout, report highlights) for what the cards do not cover. Both edit the same text.
 */
function AnnotationCard({
  text,
  onChange,
  error,
}: {
  text: string;
  onChange: (text: string) => void;
  error?: string;
}) {
  const annotation = readAnnotation(text);
  const [tab, setTab] = useState<'observations' | 'yaml'>(
    annotation.error ? 'yaml' : 'observations'
  );
  const tabClass = (on: boolean) =>
    classNames(
      'rounded-full px-4 py-1 text-base transition',
      on ? 'bg-dl-primary text-white shadow' : 'text-dl-text hover:bg-dl-tint'
    );
  return (
    <Card
      title="Annotation"
      description="What the labellers mark, how they draw it and the questions asked about it."
      actions={
        <div
          role="tablist"
          aria-label="Annotation view"
          className="inline-flex rounded-full bg-dl-surface p-1 ring-1 ring-dl-border"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'observations'}
            className={tabClass(tab === 'observations')}
            onClick={() => setTab('observations')}
          >
            Observations ({annotation.observations.length})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'yaml'}
            className={tabClass(tab === 'yaml')}
            onClick={() => setTab('yaml')}
          >
            YAML
          </button>
        </div>
      }
    >
      {tab === 'observations' && annotation.error && (
        <p role="alert" className="text-dl-danger">
          The YAML cannot be read, so the observations cannot be shown: fix it
          in the YAML tab. {annotation.error}
        </p>
      )}
      {tab === 'observations' && !annotation.error && (
        <div className="flex flex-col gap-3">
          <ObservationsEditor
            observations={observationsOf(text)}
            onChange={observations =>
              onChange(withObservations(text, observations))
            }
          />
          {annotation.levels.length > 0 && (
            <p className="text-sm text-dl-muted">
              Questions also asked on the {annotation.levels.join(', ')} (in the
              YAML tab).
            </p>
          )}
        </div>
      )}
      {tab === 'yaml' && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-dl-muted">
            The whole annotation setup: observations, characterisations
            (questions on the patient, study, series…), layout and report
            highlights.
          </p>
          <textarea
            aria-label="Annotation setup (YAML)"
            className={classNames(
              inputClassName,
              'h-auto py-2 font-mono text-sm',
              {
                'ring-dl-danger': !!annotation.error,
              }
            )}
            rows={20}
            spellCheck={false}
            value={text}
            onChange={e => onChange(e.target.value)}
          />
        </div>
      )}
      {(annotation.error || error) && (
        <FieldError text={annotation.error ?? error} />
      )}
    </Card>
  );
}

/**
 * A new task, written in a form rather than a YAML file: the common fields as fields, the annotation setup
 * as YAML with a summary and a helper to add an observation, the whole document shown as it will be sent
 * (POST /api/label-tasks — what the data managers upload today) and downloadable. `?from=<taskId>` starts
 * from a copy of that task.
 */
export function NewTaskPage() {
  const auth = useAuthorization();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const tasks = useAdminTasks();
  const [draft, setDraft] = useState<TaskDraft>(emptyDraft);
  const [origin, setOrigin] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [fromProblem, setFromProblem] = useState('');

  const from = params.get('from');
  useEffect(() => {
    if (!from) return;
    let cancelled = false;
    fetchAdminTaskDetails(auth, Number(from)).then(
      task => {
        if (!cancelled) {
          setDraft(draftFromTask(task));
          setOrigin(`a copy of ${task.name} (#${task.taskId})`);
          setFromProblem('');
        }
      },
      e =>
        !cancelled &&
        setFromProblem(
          `Could not read task #${from} to copy it (${(e as Error).message}): start from another one, or blank.`
        )
    );
    return () => {
      cancelled = true;
    };
  }, [from]); // eslint-disable-line react-hooks/exhaustive-deps

  const people = useMemo(
    () =>
      [
        ...new Set(
          (tasks.data?.content ?? []).flatMap(t => [
            ...(t.taskers ?? []),
            ...reviewersOf(t),
          ])
        ),
      ].sort(),
    [tasks.data]
  );
  const errors: DraftErrors = validate(draft);
  const shown: DraftErrors = showErrors ? errors : {};
  const yaml = toYaml(draft);
  const fileName = `${draft.name.trim().replace(/[^\w.-]+/g, '_') || 'task'}.yaml`;

  const create = useMutation({
    mutationFn: () => createAdminTask(auth, yaml, fileName),
    onSuccess: created => {
      queryClient.invalidateQueries([ADMIN_TASKS_KEY]);
      navigate(
        created?.taskId != null
          ? `/backoffice/tasks?task=${created.taskId}`
          : '/backoffice/tasks'
      );
    },
  });
  const set = (changes: Partial<TaskDraft>) =>
    setDraft(current => ({ ...current, ...changes }));
  const setPredictions = (changes: Partial<TaskDraft['predictions']>) =>
    setDraft(current => ({
      ...current,
      predictions: { ...current.predictions, ...changes },
    }));

  const submit = () => {
    setShowErrors(true);
    if (Object.keys(errors).length === 0) {
      create.mutate();
    }
  };
  const failure = create.error as
    | InstanceType<typeof TaskCreationError>
    | undefined;

  return (
    <div className="flex flex-col gap-5">
      <PageTitle title="New task" className="items-end">
        <Link
          to="/backoffice/tasks"
          className="text-lg text-dl-accent hover:underline"
        >
          ← Tasks
        </Link>
      </PageTitle>

      <StartFrom
        origin={origin}
        problem={fromProblem}
        onDraft={(next, label) => {
          setDraft(next);
          setOrigin(label);
          setFromProblem('');
          setShowErrors(false);
          create.reset();
        }}
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex min-w-0 flex-col gap-5">
          <Card title="Task">
            <div className="flex flex-col gap-4">
              <TextField
                label="Name"
                value={draft.name}
                onChange={name => set({ name })}
                error={shown.name}
                placeholder="NODULE_PLEURAL_REMAINING"
              />
              <TextField
                label="Description"
                value={draft.description}
                onChange={description => set({ description })}
                hint="One line: what the task is for, shown in the lists."
              />
              <Field
                label="Instructions"
                hint="Markdown, shown to the labellers."
              >
                <textarea
                  className={textareaClassName}
                  rows={5}
                  value={draft.instructions}
                  onChange={e => set({ instructions: e.target.value })}
                />
              </Field>
            </div>
          </Card>

          <Card
            title="Data"
            description="The BigQuery tables the items are computed from."
          >
            <div className="flex flex-col gap-4">
              <TextField
                label="Dataset table"
                mono
                value={draft.dataset}
                onChange={dataset => set({ dataset })}
                error={shown.dataset}
                placeholder="project.g3d_datasets.ln_pleural_remaining"
              />
              <TextField
                label="Earlier passes table (an iteration)"
                mono
                value={draft.initialPassesTable}
                onChange={initialPassesTable => set({ initialPassesTable })}
                error={shown.initialPassesTable}
                hint="Optional: the label passes the labellers start from, instead of or on top of the dataset."
              />
              <label className="flex items-center gap-2 text-dl-text">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-dl-primary"
                  checked={draft.predictions.enabled}
                  onChange={e => setPredictions({ enabled: e.target.checked })}
                />
                Show the AI's predictions
              </label>
              {draft.predictions.enabled && (
                <div className="flex flex-wrap items-end gap-3 rounded-2xl bg-dl-sunken p-3">
                  <div className="min-w-[20rem] flex-1">
                    <TextField
                      label="Predictions table"
                      mono
                      value={draft.predictions.table}
                      onChange={table => setPredictions({ table })}
                      placeholder="project.deeplabeling_predictions.run_filtered"
                      hint="A table or view with patientId, studyInstanceUID and seriesInstanceUID. The task keeps its own copy."
                    />
                  </div>
                  <Field label="Confidence from">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className={classNames(inputClassName, 'w-24')}
                      value={draft.predictions.minConfidence}
                      onChange={e =>
                        setPredictions({
                          minConfidence: Number(e.target.value),
                        })
                      }
                    />
                  </Field>
                  <Field label="to">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className={classNames(inputClassName, 'w-24')}
                      value={draft.predictions.maxConfidence}
                      onChange={e =>
                        setPredictions({
                          maxConfidence: Number(e.target.value),
                        })
                      }
                    />
                  </Field>
                  <Field label="Boxes">
                    <input
                      type="color"
                      className="h-10 w-14 cursor-pointer rounded-xl bg-dl-surface p-1 ring-1 ring-dl-border"
                      value={draft.predictions.boxesColor}
                      onChange={e =>
                        setPredictions({ boxesColor: e.target.value })
                      }
                    />
                  </Field>
                  <label className="flex h-10 items-center gap-2 text-dl-text">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-dl-primary"
                      checked={draft.predictions.cleanable}
                      onChange={e =>
                        setPredictions({ cleanable: e.target.checked })
                      }
                    />
                    Labellers can clean them
                  </label>
                  <FieldError text={shown.predictions} />
                </div>
              )}
            </div>
          </Card>

          <Card title="People">
            <div className="flex flex-col gap-4">
              <EmailsInput
                label="Labellers"
                value={draft.labellers}
                onChange={labellers => set({ labellers })}
                suggestions={people}
                error={shown.labellers}
              />
              <Field
                label="Labellers per item"
                hint="How many labellers label each item."
              >
                <input
                  type="number"
                  min={1}
                  className={classNames(inputClassName, 'w-24')}
                  value={draft.requiredLabelers}
                  onChange={e =>
                    set({ requiredLabelers: Number(e.target.value) })
                  }
                />
                <FieldError text={shown.requiredLabelers} />
              </Field>
              <EmailsInput
                label="Reviewers"
                value={draft.reviewers}
                onChange={reviewers => set({ reviewers })}
                suggestions={people}
                error={shown.reviewers}
                hint="Optional: who reviews the labellers' work."
              />
              {draft.reviewers.length > 0 && (
                <Field label="Cases per review">
                  <input
                    type="number"
                    min={1}
                    className={classNames(inputClassName, 'w-24')}
                    value={draft.casesPerReview}
                    onChange={e =>
                      set({ casesPerReview: Number(e.target.value) })
                    }
                  />
                </Field>
              )}
            </div>
          </Card>

          <AnnotationCard
            text={draft.annotation}
            onChange={annotation => set({ annotation })}
            error={shown.annotation}
          />

          <Card title="Viewer">
            <div className="flex flex-wrap items-end gap-6">
              <Field
                label="Sign out after (minutes idle)"
                hint="Empty: the platform's default."
              >
                <input
                  type="number"
                  min={1}
                  className={classNames(inputClassName, 'w-28')}
                  value={draft.logoutMinutes}
                  onChange={e => set({ logoutMinutes: e.target.value })}
                />
                <FieldError text={shown.logout} />
              </Field>
              <label className="flex h-10 items-center gap-2 text-dl-text">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-dl-primary"
                  checked={draft.reportsHidden}
                  onChange={e => set({ reportsHidden: e.target.checked })}
                />
                Hide the reports from the labellers
              </label>
            </div>
          </Card>
        </div>

        <div className="min-w-0">
          <div className="sticky top-4 flex flex-col gap-4">
            <Card
              title="The task file"
              description="What will be sent, as YAML: keep it with the task's notes, or reuse it."
              actions={
                <PageButton
                  className="mx-0 px-4"
                  onClick={() => {
                    const url = URL.createObjectURL(
                      new Blob([yaml], { type: 'application/x-yaml' })
                    );
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName;
                    link.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Download
                </PageButton>
              }
            >
              <pre
                aria-label="Task YAML"
                className="max-h-[32rem] overflow-auto rounded-2xl bg-dl-sunken p-4 font-mono text-xs leading-relaxed text-dl-text"
              >
                {yaml}
              </pre>
            </Card>
            {showErrors && Object.keys(errors).length > 0 && (
              <p
                role="alert"
                className="rounded-2xl bg-dl-danger-tint px-4 py-3 text-dl-danger ring-1 ring-dl-danger/30"
              >
                Some fields need a look: {Object.values(errors).join(' ')}
              </p>
            )}
            {failure && (
              <div
                role="alert"
                className="rounded-2xl bg-dl-danger-tint px-4 py-3 text-dl-danger ring-1 ring-dl-danger/30"
              >
                <p>viewer-api refused the task — {failure.message}</p>
                {failure.violations?.length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-sm">
                    {failure.violations.map(v => (
                      <li key={v.field + v.message}>
                        <code>{v.field}</code>: {v.message}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <Link
                to="/backoffice/tasks"
                className="px-3 text-dl-accent hover:underline"
              >
                Cancel
              </Link>
              <PageButton
                tone="go"
                className="mx-0 px-6 py-2 text-lg"
                onClick={submit}
                disabled={create.isLoading}
              >
                {create.isLoading ? 'Creating…' : 'Create task'}
              </PageButton>
            </div>
            <p className="text-sm text-dl-muted">
              Creating the task computes its items from the dataset table; the
              labellers see it once its predictions, if any, are imported.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewTaskPage;
